import { useEffect, useState } from 'react'

import { ArrowUpRight } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { Modal } from '@/components/modal/Modal'
import { ModalContainer } from '@/components/modal/ModalContainer'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'
import { ModalHeader } from '@/components/modal/ModalHeader'
import { Typeahead } from '@/components/typeahead/Typeahead'
import type { MatterAssignableRole } from './schemas'

import { InvitationRoleMenu } from './InvitationRoleMenu'
import { InvitationRoleHint } from './InvitationRoleHint'
import { MemberRow } from './MemberRow'
import { RemoveMemberConfirmModal } from './RemoveMemberConfirmModal'
import { TransferOwnershipConfirmModal } from './TransferOwnershipConfirmModal'
import { RolesPermissionsPopover } from './RolesPermissionsPopover'
import { AddMembersStatus, MemberListStatus, type MatterCollaborator, type OrgUserOption, type RemoveMemberResult } from './types'

interface ManageMembersModalProps {
  open: boolean
  onClose: () => void
  matterName: string
  currentUserId: string
  collaborators: MatterCollaborator[]
  listStatus: MemberListStatus
  /** Full org directory; users already on the matter are filtered out here. */
  orgUsers: OrgUserOption[]
  onAddMembers: (userIds: string[], role?: MatterAssignableRole) => void
  editorEnabled?: boolean
  onChangeRole?: (userId: string, role: MatterAssignableRole) => Promise<void>
  roleChangeUserId?: string | null
  roleChangeError?: string | null
  addMembersStatus: AddMembersStatus
  onRemoveMember: (userId: string) => Promise<RemoveMemberResult>
  /** Add-flow failure message, shown above the footer actions. */
  actionError: string | null
  /** Navigates to org management; passed only for org admins. When omitted, the empty-state link is hidden. */
  onManageOrganization?: () => void
  /** Seeds the pending add selection; used by stories/tests. */
  initialSelectedUserIds?: string[]
  /** Whether the viewer can add/remove members. Collaborators get a view-only "Members" list. */
  canEdit: boolean
  canTransferOwnership: boolean
  onTransferOwnership: (userId: string) => Promise<string | null>
}

export function ManageMembersModal({
  open,
  onClose,
  matterName,
  currentUserId,
  collaborators,
  listStatus,
  orgUsers,
  onAddMembers,
  addMembersStatus,
  onRemoveMember,
  actionError,
  onManageOrganization,
  initialSelectedUserIds,
  canEdit,
  canTransferOwnership,
  onTransferOwnership,
  editorEnabled = false,
  onChangeRole,
  roleChangeUserId = null,
  roleChangeError = null,
}: ManageMembersModalProps) {
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(initialSelectedUserIds ?? [])
  const [memberToRemove, setMemberPendingRemoval] = useState<MatterCollaborator | null>(null)
  const [memberToTransfer, setMemberToTransfer] = useState<MatterCollaborator | null>(null)
  const [invitationRole, setInvitationRole] = useState<MatterAssignableRole>('collaborator')
  const effectiveInvitationRole = editorEnabled ? invitationRole : 'collaborator'

  useEffect(() => {
    if (!open || !editorEnabled) setInvitationRole('collaborator')
  }, [open, editorEnabled])

  // Prune the pending selection as added users land in the member list
  // (also covers partial success on multi-add).
  useEffect(() => {
    setSelectedUserIds((prev) => {
      const next = prev.filter((id) => !collaborators.some((c) => c.userId === id))
      return next.length === prev.length ? prev : next
    })
  }, [collaborators])

  const sortedCollaborators = [...collaborators].sort((a) => (a.matterRole === 'owner' ? -1 : 1))

  const typeaheadItems = orgUsers
    .filter((user) => !collaborators.some((c) => c.userId === user.userId))
    .map((user) => ({ id: user.userId, label: user.displayName, description: user.email }))

  // Org admins get a shortcut to invite people who aren't in the org yet; others don't see it.
  const manageOrgAction = onManageOrganization ? (
    <Button variant="plain" color="primary" size="sm" trailingIcon={<ArrowUpRight className="h-3.5 w-3.5" />} onClick={onManageOrganization}>
      Manage Organization
    </Button>
  ) : undefined

  return (
    <>
      <Modal open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
        <ModalContainer width="lg" height="max-lg" className="min-h-[456px] max-w-[568px]">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col pt-6 max-h-[90vh]">
            {/* Header */}
            <div className="shrink-0 px-6 pb-4">
              <ModalHeader title={canEdit ? 'Manage members' : 'Members'} showCloseButton />
            </div>

            {/* Matter name */}
            <div className="flex shrink-0 items-center gap-2 px-6">
              <p className="min-w-0 flex-1 truncate text-sm font-medium leading-5 tracking-[-0.15px] text-brand-neutral-600">{matterName}</p>
              {canEdit && editorEnabled && (
                <InvitationRoleMenu role={effectiveInvitationRole} onChange={setInvitationRole} disabled={addMembersStatus === AddMembersStatus.LOADING} />
              )}
              {!canEdit && (
                <RolesPermissionsPopover editorEnabled={editorEnabled} hasEditorAssignments={collaborators.some((member) => member.matterRole === 'editor')} />
              )}
            </div>

            {/* Typeahead */}
            {canEdit && (
              <div className="shrink-0 px-6 pt-4">
                <Typeahead
                  items={typeaheadItems}
                  selectedIds={selectedUserIds}
                  onChange={setSelectedUserIds}
                  placeholder="Search your organization"
                  ariaLabel="Search your organization"
                  emptyStateTitle="Everyone in your organization is already on this matter"
                  noResultsTitle='No results for "{searchTerm}" in your organization'
                  noResultsDescription="Only members of your organization can be added to a matter. Invite them to your organization first."
                  emptyStateAction={manageOrgAction}
                  selectAllLabel="Select all {count} members"
                  modal
                />
                <div className="mt-2">
                  <InvitationRoleHint role={effectiveInvitationRole} />
                </div>
              </div>
            )}

            <div className="mt-4 flex min-h-0 flex-1 flex-col">
              <div className="shrink-0 border-b border-brand-neutral-200 px-6 pb-2">
                <p className="text-xs font-semibold uppercase text-brand-neutral-600">Who has access</p>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto">
                {listStatus === MemberListStatus.LOADING && (
                  <div className="flex justify-center py-8">
                    <CircularProgressContinuousSized size={24} thickness={5} />
                  </div>
                )}

                {listStatus === MemberListStatus.ERROR && (
                  <div className="px-6 py-4">
                    <InlineMessage variant="error">Could not load members. Close this dialog and try again.</InlineMessage>
                  </div>
                )}

                {listStatus === MemberListStatus.SUCCESS &&
                  sortedCollaborators.map((collaborator) => (
                    <MemberRow
                      key={collaborator.userId}
                      displayName={collaborator.displayName}
                      email={collaborator.email}
                      isOwnerRow={collaborator.matterRole === 'owner'}
                      matterRole={collaborator.matterRole}
                      canChangeRole={
                        canEdit && collaborator.matterRole !== 'owner' && !!onChangeRole && (editorEnabled || collaborator.matterRole === 'editor')
                      }
                      roleChanging={roleChangeUserId === collaborator.userId}
                      actionsDisabled={roleChangeUserId !== null}
                      onChangeRole={(role) => {
                        void onChangeRole?.(collaborator.userId, role)
                      }}
                      isCurrentUser={collaborator.userId === currentUserId}
                      canRemove={canEdit && collaborator.matterRole !== 'owner'}
                      canTransfer={canEdit && canTransferOwnership && collaborator.matterRole !== 'owner'}
                      isPendingAcceptance={collaborator.matterRole !== 'owner' && collaborator.addedAt === ''}
                      onRemove={() => setMemberPendingRemoval(collaborator)}
                      onTransfer={() => setMemberToTransfer(collaborator)}
                    />
                  ))}
              </div>
            </div>

            {/* Footer - border only in empty state (just the owner) */}
            <div className={`mt-auto shrink-0 p-6 ${sortedCollaborators.length === 1 ? 'border-t border-brand-neutral-200' : ''}`}>
              <ModalFooterWithActions
                inlineMessage={roleChangeError || actionError ? { variant: 'error', children: roleChangeError || actionError } : undefined}
                secondaryAction={{ label: 'Done', onClick: onClose }}
                primaryAction={
                  canEdit
                    ? {
                        label: 'Add Members',
                        onClick: () => onAddMembers(selectedUserIds, effectiveInvitationRole),
                        disabled: selectedUserIds.length === 0,
                        loading: addMembersStatus === AddMembersStatus.LOADING,
                      }
                    : undefined
                }
              />
            </div>
          </div>
        </ModalContainer>
      </Modal>

      {memberToRemove && (
        <RemoveMemberConfirmModal
          memberName={memberToRemove.displayName}
          matterName={matterName}
          onClose={() => setMemberPendingRemoval(null)}
          onConfirm={() => onRemoveMember(memberToRemove.userId)}
        />
      )}
      {memberToTransfer && canTransferOwnership && (
        <TransferOwnershipConfirmModal
          memberName={memberToTransfer.displayName}
          matterName={matterName}
          onClose={() => setMemberToTransfer(null)}
          onConfirm={() => onTransferOwnership(memberToTransfer.userId)}
        />
      )}
    </>
  )
}
